import { useState, useRef, useMemo } from 'react'
import { Bot, Check, CheckCheck, Clock, AlertCircle, X, UserCheck, Download } from 'lucide-react'
import { useSwipe } from '@/hooks/useSwipe'
import { haptics } from '@/utils/haptics'
import { MessageReactions } from '@/components/messages/MessageReactions'
import { DeletedMessage } from '@/components/messages/DeletedMessage'
import { getBubbleTheme } from './chatTheme'
import { resolveMediaUrl } from '@/utils/imageUrl'

interface MessageBubbleProps {
  message: any
  repliedMessage?: any
  direction: 'in' | 'out'
  /** Primer mensaje de un grupo consecutivo del mismo emisor (muestra la "colita"). */
  isFirstInGroup?: boolean
  onReply?: () => void
  onDelete?: (messageId: string) => void
  onCancelScheduled?: (messageId: string) => void
  onReaction?: (messageId: string, reaction: string) => void
  onImageClick?: (url: string, alt?: string) => void
  formatTime: (date: Date | string | number) => string
  mediaLoadState?: { loading: boolean; error: boolean }
  onMediaLoad?: () => void
  onMediaError?: () => void
}

export function MessageBubble({
  message,
  repliedMessage,
  direction,
  isFirstInGroup = true,
  onReply,
  onDelete,
  onCancelScheduled,
  onReaction,
  onImageClick,
  formatTime,
  mediaLoadState,
  onMediaLoad,
  onMediaError,
}: MessageBubbleProps) {
  const [showActions, setShowActions] = useState(false)
  const [replyIndicator, setReplyIndicator] = useState(false)
  const longPressTimer = useRef<NodeJS.Timeout>()

  const isScheduled = message.status === 'scheduled'
  const isCancelled = message.status === 'cancelled'
  const isFailed = message.status === 'failed'
  const isBotMessageId =
    typeof message?.messageId === 'string' &&
    (message.messageId.startsWith('bot_') ||
      message.messageId.startsWith('bot-') ||
      message.messageId.startsWith('offhours_') ||
      message.messageId.startsWith('welcome_'))
  const isSystem = message.source === 'system' || message.status === 'escalated'
  const isBotAuthored =
    direction === 'out' &&
    !isSystem &&
    (isBotMessageId ||
      message?.source === 'ai' ||
      message?.source === 'bot' ||
      message?.source === 'welcome' ||
      message?.source === 'confirmation' ||
      message?.source === 'product' ||
      message?.source === 'faq' ||
      message?.source === 'rule' ||
      message?.source === 'off_hours_message' ||
      (Boolean(message?.source) &&
        message.source !== 'human' &&
        message.source !== 'agent' &&
        message.source !== 'user' &&
        message.source !== 'system'))
  const theme = getBubbleTheme({ direction, isBotAuthored: !!isBotAuthored, isScheduled, isCancelled, isFailed })

  if (isSystem) {
    return (
      <div className="flex justify-center my-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-100 px-3.5 py-1 text-xs font-semibold text-purple-900 border border-purple-200 shadow-2xs">
          <Bot className="h-3.5 w-3.5 text-purple-700" />
          <span>{message.content || 'Conversación escalada a vendedor humano'}</span>
        </span>
      </div>
    )
  }

  const effectiveMediaUrl = useMemo(() => resolveMediaUrl(message.mediaUrl), [message.mediaUrl])
  const cleanAudioMime = useMemo(() => {
    if (!message.mediaMimetype) return 'audio/ogg'
    return message.mediaMimetype.split(';')[0].trim() || 'audio/ogg'
  }, [message.mediaMimetype])

  const effectiveType = useMemo(() => {
    if (message.type && message.type !== 'text') {
      // Si fue marcado como audio pero claramente es una imagen de bot/producto o URL de imagen
      if (message.type === 'audio') {
        const isBotImg =
          (typeof message.messageId === 'string' && (message.messageId.startsWith('bot_img') || message.messageId.startsWith('img_'))) ||
          (typeof message.content === 'string' && (message.content.startsWith('[Imagen:') || message.content.startsWith('[Foto'))) ||
          message.source === 'product' ||
          (typeof message.mediaUrl === 'string' && (
            message.mediaUrl.match(/\.(jpe?g|png|webp|gif|svg)(\?.*)?$/i) ||
            message.mediaUrl.includes('photo') ||
            message.mediaUrl.includes('image') ||
            message.mediaUrl.includes('unsplash') ||
            message.mediaUrl.includes('/storage/products/')
          ))
        if (isBotImg) return 'image'
      }
      return message.type
    }

    if (message.hasMedia) {
      if (
        (typeof message.messageId === 'string' && (message.messageId.startsWith('bot_img') || message.messageId.startsWith('img_'))) ||
        (typeof message.content === 'string' && (message.content.startsWith('[Imagen:') || message.content.startsWith('[Foto'))) ||
        message.source === 'product' ||
        (typeof message.mediaUrl === 'string' && (
          message.mediaUrl.match(/\.(jpe?g|png|webp|gif|svg)(\?.*)?$/i) ||
          message.mediaUrl.includes('photo') ||
          message.mediaUrl.includes('image') ||
          message.mediaUrl.includes('unsplash') ||
          message.mediaUrl.includes('/storage/products/')
        ))
      ) {
        return 'image'
      }
      if (message.mediaMimetype?.startsWith('audio/') || (!direction || direction === 'in')) {
        return 'audio'
      }
    }
    return message.type || 'text'
  }, [message.type, message.hasMedia, message.messageId, message.content, message.mediaUrl, message.mediaMimetype, message.source, direction])

  // Swipe para responder (solo mensajes entrantes)
  const { swipeState, handlers } = useSwipe({
    onSwipeRight: direction === 'in' ? () => {
      haptics.impact('medium')
      setReplyIndicator(true)
      setTimeout(() => {
        setReplyIndicator(false)
        onReply?.()
      }, 200)
    } : undefined,
    threshold: 60,
  })

  // Long press para menú
  const handleTouchStart = (e: React.TouchEvent) => {
    handlers.onTouchStart(e)
    longPressTimer.current = setTimeout(() => {
      haptics.impact('heavy')
      setShowActions(true)
    }, 500)
  }

  const handleTouchEnd = () => {
    handlers.onTouchEnd()
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
    }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    handlers.onTouchMove(e)
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
    }
  }

  // Identificar la reacción propia (del agente/dashboard) vs la del cliente
  const contactPhone = (direction === 'in' ? message.waFrom : message.waTo) || ''
  const contactDigits = contactPhone.replace(/\D+/g, '')
  const myReaction = useMemo(() => {
    if (!message.reactions || message.reactions.length === 0) return undefined
    return message.reactions.find((r: any) => {
      const rDigits = (r.from || '').replace(/@.*/, '').replace(/\D+/g, '')
      if (contactDigits && rDigits === contactDigits) return false
      return true
    })
  }, [message.reactions, contactDigits])

  // Double tap / double click para reaccionar
  const [lastTap, setLastTap] = useState(0)
  const handleDoubleTap = (e: React.MouseEvent | React.TouchEvent) => {
    // Si el clic fue en un elemento interactivo hijo (botón, link, audio, etc.), no intervenir
    const target = e.target as HTMLElement | null
    if (target?.closest('button, a, audio, video, img, input, textarea')) {
      return
    }

    const now = Date.now()
    if (now - lastTap < 350) {
      setLastTap(0) // Resetea para no encadenar triples toques accidentales
      haptics.notification('success')
      // Comportamiento idéntico a WhatsApp:
      // Si ya reaccioné con ❤️, el doble clic la QUITA (toggle off)
      if (myReaction?.emoji === '❤️') {
        onReaction?.(message.messageId, '')
      } else {
        // Si no tengo reacción o tengo una diferente, aplicar ❤️
        onReaction?.(message.messageId, '❤️')
      }
      return
    }
    setLastTap(now)
  }

  const getStatusIcon = () => {
    // Scheduled message status
    if (isScheduled) {
      return (
        <span title="Programado">
          <Clock className="h-3.5 w-3.5 text-blue-500" />
        </span>
      )
    }
    if (isCancelled) {
      return (
        <span title="Cancelado">
          <X className="h-3.5 w-3.5 text-red-400" />
        </span>
      )
    }
    if (isFailed || message.status === 'failed') {
      return (
        <span title="Error de envío">
          <AlertCircle className="h-3.5 w-3.5 text-rose-600" />
        </span>
      )
    }

    // 1. WhatsApp ACK numérico
    if (message.ack !== undefined && message.ack !== null) {
      switch (message.ack) {
        case -1:
          return (
            <span title="Error al enviar">
              <AlertCircle className="h-3.5 w-3.5 text-rose-500" />
            </span>
          )
        case 0:
          return (
            <span title="Pendiente de envío">
              <Clock className="h-3.5 w-3.5 text-slate-400" />
            </span>
          )
        case 1:
          return (
            <span title="Enviado al servidor">
              <Check className="h-3.5 w-3.5 text-slate-400" />
            </span>
          )
        case 2:
          return (
            <span title="Entregado al contacto">
              <CheckCheck className="h-3.5 w-3.5 text-slate-400" />
            </span>
          )
        case 3:
        case 4:
          return (
            <span title="Leído por el contacto">
              <CheckCheck className="h-3.5 w-3.5 text-sky-500" />
            </span>
          )
      }
    }

    // 2. Status textual de la base de datos
    if (message.status) {
      switch (message.status) {
        case 'read':
          return (
            <span title="Leído">
              <CheckCheck className="h-3.5 w-3.5 text-sky-500" />
            </span>
          )
        case 'delivered':
          return (
            <span title="Entregado">
              <CheckCheck className="h-3.5 w-3.5 text-slate-400" />
            </span>
          )
        case 'sent':
          return (
            <span title="Enviado">
              <Check className="h-3.5 w-3.5 text-slate-400" />
            </span>
          )
        case 'pending':
          return (
            <span title="Pendiente de envío">
              <Clock className="h-3.5 w-3.5 text-slate-400" />
            </span>
          )
      }
    }

    // 3. Fallback para mensajes salientes ya registrados
    return (
      <span title="Enviado">
        <Check className="h-3.5 w-3.5 text-slate-400" />
      </span>
    )
  }

  const getReplyPreview = () => {
    if (!repliedMessage) return ''
    if (repliedMessage.content) return repliedMessage.content
    if (repliedMessage.type === 'image') return '📷 Imagen'
    if (repliedMessage.type === 'audio') return '🎵 Audio'
    if (repliedMessage.type === 'video') return '🎥 Video'
    if (repliedMessage.type === 'document') return `📄 ${repliedMessage.mediaFilename || 'Documento'}`
    return ''
  }

  const swipeTransform = swipeState.isSwiping && direction === 'in'
    ? `translateX(${Math.min(80, Math.max(0, swipeState.swipeDistance))}px)`
    : replyIndicator
    ? 'translateX(20px)'
    : 'translateX(0)'

  const formatScheduledTime = (timestamp: Date | string | number | null | undefined) => {
    if (!timestamp) return ''
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp)
    if (isNaN(date.getTime())) return ''
    return new Intl.DateTimeFormat('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: 'short',
    }).format(date)
  }

  return (
    <div className={`flex ${direction === 'out' ? 'justify-end' : 'justify-start'} group relative`}>
      {/* Reply indicator */}
      {direction === 'in' && swipeState.isSwiping && swipeState.swipeDistance > 20 && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-400 transition-opacity">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
          </svg>
        </div>
      )}

      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleDoubleTap}
        className={`max-w-[85%] sm:max-w-[75%] lg:max-w-[65%] px-3 py-2 rounded-xl transition-all relative ${
          isFirstInGroup ? (direction === 'out' ? 'rounded-tr-sm' : 'rounded-tl-sm') : ''
        } ${theme.bubble} ${isCancelled ? 'opacity-75' : ''}`}
        data-sender={theme.sender}
        style={{
          transform: swipeTransform,
          transition: swipeState.isSwiping ? 'none' : 'transform 0.2s ease-out',
        }}
      >

        {/* Actions menu */}
        {showActions && (
          <>
            <div className="fixed inset-0 z-50" onClick={() => setShowActions(false)} />
            <div className="absolute top-0 right-0 z-50 bg-white rounded-xl shadow-xl border border-gray-200 p-2 flex flex-col gap-1.5 min-w-[210px]">
              {/* Barra de reacciones rápidas */}
              <div className="flex items-center justify-between gap-1 px-1 py-1 border-b border-gray-100">
                {['👍', '❤️', '😂', '😮', '😢', '🙏'].map((emoji) => {
                  const isSelected = myReaction?.emoji === emoji
                  return (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => {
                        if (isSelected) {
                          onReaction?.(message.messageId, '')
                        } else {
                          onReaction?.(message.messageId, emoji)
                        }
                        setShowActions(false)
                      }}
                      className={`text-xl p-1 rounded-lg hover:scale-125 transition-all ${
                        isSelected
                          ? 'bg-emerald-100 ring-2 ring-emerald-400 scale-110 shadow-xs'
                          : 'hover:bg-gray-100'
                      }`}
                      title={isSelected ? `Quitar reacción ${emoji}` : `Reaccionar con ${emoji}`}
                    >
                      {emoji}
                    </button>
                  )
                })}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    onReply?.()
                    setShowActions(false)
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg text-sm text-gray-700"
                >
                  Responder
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(message.content || '')
                    haptics.selection()
                    setShowActions(false)
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg text-sm text-gray-700"
                >
                  Copiar
                </button>
                {direction === 'out' && onDelete && !isScheduled && (
                  <button
                    onClick={() => {
                      onDelete(message.messageId)
                      setShowActions(false)
                    }}
                    className="p-2 hover:bg-red-50 text-red-600 rounded-lg text-sm"
                  >
                    Eliminar
                  </button>
                )}
                {direction === 'out' && isScheduled && onCancelScheduled && (
                  <button
                    onClick={() => {
                      onCancelScheduled(message.messageId)
                      setShowActions(false)
                    }}
                    className="p-2 hover:bg-red-50 text-red-600 rounded-lg text-sm"
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </div>
          </>
        )}

        {/* Etiqueta de remitente para mensajes salientes */}
        {direction === 'out' && isBotAuthored && (
          <div className="mb-1 flex items-center gap-1">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-violet-200/80 text-violet-800 tracking-wide border border-violet-300/40">
              <Bot className="h-3 w-3 text-violet-700" />
              Bot IA
            </span>
          </div>
        )}

        {direction === 'out' && !isBotAuthored && (
          <div className="mb-1 flex items-center gap-1">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-200/80 text-emerald-900 tracking-wide border border-emerald-300/40">
              <UserCheck className="h-3 w-3 text-emerald-700" />
              Equipo (Vendedor)
            </span>
          </div>
        )}

        {/* Replied message preview */}
        {repliedMessage && (
          <div className={`mb-1.5 px-2 py-1 border-l-4 rounded-md ${theme.quote}`}>
            <p className={`text-[10px] font-semibold mb-0.5 ${theme.quoteName}`}>
              {repliedMessage.direction === 'out' ? 'Tú' : 'Contacto'}
            </p>
            <p className={`text-xs truncate ${theme.quoteText}`}>
              {getReplyPreview()}
            </p>
          </div>
        )}

        {/* Message content */}
        {message.isRevoked ? (
          <DeletedMessage direction={direction} />
        ) : isCancelled ? (
          <p className="text-sm italic opacity-60">Mensaje cancelado</p>
        ) : message.hasMedia && (effectiveMediaUrl || message.mediaUrl) ? (
          <div className="space-y-1">
            {mediaLoadState?.error ? (
              <div className="flex flex-col gap-1 py-1 text-xs">
                <div className="flex items-center gap-1.5 text-rose-600 font-medium">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  <span>Error al cargar multimedia</span>
                </div>
                {effectiveMediaUrl && (
                  <a
                    href={effectiveMediaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={message.mediaFilename || (effectiveType === 'audio' ? 'audio.ogg' : 'archivo')}
                    className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                  >
                    <Download className="h-3 w-3" /> Descargar archivo
                  </a>
                )}
              </div>
            ) : (
              <>
                {effectiveType === 'image' && (
                  <div className="relative">
                    {mediaLoadState?.loading && (
                      <div className="w-full h-32 sm:h-40 lg:h-48 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
                    )}
                    {effectiveMediaUrl ? (
                      <img
                        src={effectiveMediaUrl}
                        alt={message.mediaFilename || message.content || 'Imagen'}
                        className={`max-w-full max-h-64 sm:max-h-72 lg:max-h-80 rounded-lg cursor-pointer object-cover ${
                          mediaLoadState?.loading ? 'hidden' : 'block'
                        }`}
                        onClick={() => onImageClick?.(effectiveMediaUrl, message.mediaFilename)}
                        onLoad={onMediaLoad}
                        onError={onMediaError}
                      />
                    ) : (
                      <div className="flex items-center gap-2 py-2 px-3 bg-gray-100 dark:bg-gray-800 rounded-lg text-xs text-gray-500">
                        <AlertCircle className="h-4 w-4 shrink-0 text-gray-400" />
                        <span>Imagen no disponible para vista previa</span>
                      </div>
                    )}
                  </div>
                )}
                {effectiveType === 'audio' && (
                  <div className="w-full min-w-[220px] max-w-sm py-0.5">
                    <audio
                      controls
                      preload="metadata"
                      className="w-full h-10 rounded-md"
                      onCanPlay={onMediaLoad}
                      onLoadedMetadata={onMediaLoad}
                      onError={onMediaError}
                    >
                      <source src={effectiveMediaUrl} type={cleanAudioMime} />
                      <source src={effectiveMediaUrl} type="audio/ogg" />
                      <source src={effectiveMediaUrl} type="audio/mpeg" />
                      <source src={effectiveMediaUrl} />
                      Tu navegador no soporta la reproducción de audio.
                    </audio>
                  </div>
                )}
                {effectiveType === 'video' && (
                  <video
                    controls
                    preload="metadata"
                    src={effectiveMediaUrl}
                    className="w-full rounded-lg"
                    onLoadedData={onMediaLoad}
                    onError={onMediaError}
                  >
                    <source src={effectiveMediaUrl} type={message.mediaMimetype} />
                  </video>
                )}
                {effectiveType === 'document' && (
                  <a
                    href={effectiveMediaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm underline flex items-center gap-1.5"
                  >
                    <Download className="h-3.5 w-3.5 shrink-0" />
                    <span>{message.mediaFilename || 'Documento'}</span>
                  </a>
                )}
              </>
            )}
            {message.content && (
              <p className={`text-sm leading-relaxed whitespace-pre-wrap break-words ${theme.text}`}>{message.content}</p>
            )}
          </div>
        ) : (
          <p className={`text-sm leading-relaxed whitespace-pre-wrap break-words ${theme.text}`}>{message.content}</p>
        )}

        {/* Reactions */}
        <MessageReactions
          messageId={message.messageId || message.id}
          reactions={message.reactions || []}
          myReactionEmoji={myReaction?.emoji}
          onToggleReaction={(emoji) => {
            // Si hago clic en el emoji que ya tengo seleccionado, lo quito (toggle off)
            if (myReaction?.emoji === emoji) {
              onReaction?.(message.messageId, '')
            } else {
              onReaction?.(message.messageId, emoji)
            }
          }}
        />

        {/* Time and status */}
        <div className={`flex items-center justify-end gap-1.5 mt-1 ${theme.meta}`}>

          {/* Scheduled indicator */}
          {isScheduled && message.scheduledAt && (
            <span className="flex items-center bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-[10px] font-medium gap-1">
              <Clock className="h-2.5 w-2.5" />
              {formatScheduledTime(message.scheduledAt)}
            </span>
          )}

          <span className="text-[11px] font-medium tabular-nums">
            {formatTime(message.timestamp || message.createdAt)}
          </span>
          {direction === 'out' && getStatusIcon()}
        </div>
      </div>
    </div>
  )
}
